import { debounce } from "lodash";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FormattedMessage } from "react-intl";
import { Box, Typography } from "@mui/material";
import { makeStyles } from "@mui/styles";
import WorkspaceWidgetComponent from "../../../widgets/WorkspaceWidget/WorkspaceWidgetComponent";
import { defaultIconSiteId, defaultIconStyling } from "../../../widgets/WorkspaceWidget/helper";
import TextPropertyField from "./TextPropertyField";

const useStyles = makeStyles(() => ({
  settingsContainer: {
    border: "1px solid #e1e1e1",
    borderRadius: 5,
    marginTop: "1em",
    padding: 5,
    paddingTop: 10,
    position: "relative",
  },
  textLabel: {
    background: "#fff",
    fontSize: 12,
    color: "#bdb4b4",
    position: "absolute",
    left: 15,
    top: -9,
    minWidth: 80,
    textAlign: "center",
  },
}));

const getSettingsFromLayout = (layout) => {
  const { contentBGhoverColor, iconStyling } = layout || {};

  return {
    contentBGhoverColor,
    iconStyling: { ...iconStyling },
  };
};

const MyWorkspacesWidgetSettings = ({ onDelayedUpdate, selectedWidget }) => {
  const classes = useStyles();

  const [isDirty, setIsDirty] = useState(false);
  const [localLayout, setLocalLayout] = useState();
  const [localSettings, setLocalSettings] = useState();

  const workspaces = useMemo(
    () => (
      Array(3)
        .fill({
          id: defaultIconSiteId,
          name: <FormattedMessage id="widget.myWorkspaces.preview.item.name" />,
          description: <FormattedMessage id="widget.myWorkspaces.preview.item.description" />,
        })
    ),
    []
  );

  const debouncedIconStylingPropertyChange = useCallback(
    debounce((name, value) => {
      setLocalSettings((prev) => ({
        ...prev,
        iconStyling: {
          ...prev.iconStyling,
          [name]: value || undefined,
        },
      }));
      setIsDirty(true);
    }, 250),
    []
  );

  const debouncedPropertyChange = useCallback(
    debounce((name, value) => {
      setLocalSettings((prev) => ({
        ...prev,
        [name]: value || undefined,
      }));
      setIsDirty(true);
    }, 250),
    []
  );

  useEffect(() => {
    if (selectedWidget) {
      if (!localSettings) {
        setLocalLayout(selectedWidget);
      }
      else {
        const { contentBGhoverColor, iconStyling } = localSettings;
        setLocalLayout({
          ...selectedWidget,
          contentBGhoverColor,
          iconStyling: {
            ...selectedWidget.iconStyling,
            ...iconStyling
          }
        });
      }
    }
    if (!localSettings) {
      const { contentBGhoverColor, iconStyling } = getSettingsFromLayout(selectedWidget);
      setLocalSettings({
        contentBGhoverColor,
        iconStyling: { ...iconStyling },
      });
    }
  }, [selectedWidget, selectedWidget.contentBG, selectedWidget.dividerColor, selectedWidget.name, selectedWidget.titleColor]);

  useEffect(() => {
    if (localSettings) {
      const { contentBGhoverColor, iconStyling } = localSettings;
      setLocalLayout((prev) => ({
        ...prev,
        contentBGhoverColor,
        iconStyling: {
          ...prev.iconStyling,
          ...iconStyling
        }
      }));
    }
  }, [localSettings]);

  useEffect(() => {
    if (localLayout && isDirty) {
      onDelayedUpdate?.(localSettings);
      setIsDirty(false);
    }
  }, [localLayout, isDirty]);

  if (!localSettings) {
    return;
  }

  return (
    <>
      <Box className={classes.settingsContainer}>
        <Typography className={classes.textLabel}>
          <FormattedMessage id="widget.myWorkspaces.preview" />
        </Typography>
        <Box sx={{ backgroundColor: selectedWidget?.bgColor || "#ffffff" }}>
          <WorkspaceWidgetComponent
            layout={localLayout}
            workspaces={workspaces}
          />
        </Box>
      </Box>
      <Box className={classes.settingsContainer}>
        <Typography className={classes.textLabel}>
          <FormattedMessage id="widget.myWorkspaces.iconStyling.groupLabel" />
        </Typography>
        <Box sx={{
          padding: 1,
          paddingBottom: 0,
          paddingTop: 1.5,
          "& > .MuiTextField-root": {
            marginBottom: 1.5,
          }
        }}>
          <TextPropertyField
            initialValue={localSettings.iconStyling.borderColor}
            label={<FormattedMessage id="widget.myWorkspaces.iconStyling.propLabel.borderColor" />}
            onChange={(value) => debouncedIconStylingPropertyChange("borderColor", value)}
            placeholder={defaultIconStyling.borderColor}
          />
          <TextPropertyField
            initialValue={localSettings.iconStyling.borderWidth}
            label={<FormattedMessage id="widget.myWorkspaces.iconStyling.propLabel.borderWidth" />}
            onChange={(value) => debouncedIconStylingPropertyChange("borderWidth", value)}
            placeholder={defaultIconStyling.borderWidth}
          />
          <TextPropertyField
            initialValue={localSettings.iconStyling.borderRadius}
            label={<FormattedMessage id="widget.myWorkspaces.iconStyling.propLabel.borderRadius" />}
            onChange={(value) => debouncedIconStylingPropertyChange("borderRadius", value)}
            placeholder={defaultIconStyling.borderRadius}
          />
          <TextPropertyField
            initialValue={localSettings.iconStyling.boxShadow}
            label={<FormattedMessage id="widget.myWorkspaces.iconStyling.propLabel.boxShadow" />}
            onChange={(value) => debouncedIconStylingPropertyChange("boxShadow", value)}
            placeholder={defaultIconStyling.boxShadow}
          />
        </Box>
      </Box>
      <Box sx={{
        padding: 0,
        paddingTop: 1.5,
      }}>
        <TextPropertyField
          initialValue={localSettings.contentBGhoverColor}
          label={<FormattedMessage id="widget.myWorkspaces.propLabel.backgroundHoverColor" />}
          onChange={(value) => debouncedPropertyChange("contentBGhoverColor", value)}
          placeholder={defaultIconStyling.contentBGhoverColor}
        />
      </Box>
    </>
  );
};

export default MyWorkspacesWidgetSettings;
